import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const FEEDBACK_DURATION = 1500;
const ADD_PENDING_TIMEOUT = 10000;
/** CartUpdateEvent data.source emitted by the add-to-cart path (assets/product-form.js). */
const ADD_SOURCE = 'product-form-component';

/**
 * Is the element rendered and currently within the viewport?
 *
 * @param {Element | null} el
 * @returns {boolean}
 */
function isVisible(el) {
  if (!el) return false;
  const style = getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none' || parseFloat(style.opacity) === 0) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
}

/**
 * Is the element laid out at all? Deliberately ignores viewport position, unlike isVisible — the
 * main ATC is normally below the fold when the pill initialises, so a viewport test would reject
 * the very button we are looking for.
 *
 * @param {Element | null} el
 * @returns {boolean}
 */
function isRendered(el) {
  if (!el) return false;
  const style = getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * The button the pill proxies to. Configurator templates scope it; elsewhere the page can hold
 * several .add-to-cart-button instances (quick-add cards, hidden variants), so pick the first one
 * that is actually rendered rather than blindly taking the first in DOM order.
 *
 * @returns {HTMLElement | null}
 */
function resolveMainAtc() {
  const scoped = document.querySelector(
    '.product-configurator .add-to-cart-button, .product-configurator-e .add-to-cart-button'
  );
  if (scoped) return /** @type {HTMLElement} */ (scoped);

  const candidates = /** @type {HTMLElement[]} */ ([...document.querySelectorAll('.add-to-cart-button')]);
  return candidates.find(isRendered) ?? candidates[0] ?? null;
}

/**
 * @typedef {object} StickyAtcRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} label
 * @property {HTMLElement} [price]
 * @property {HTMLElement} [compare]
 */

/**
 * <sticky-atc> — the bottom-fixed pill that duplicates the PDP's main add-to-cart once the real
 * button has scrolled away. It has no form of its own: clicking it proxies a click to the main
 * ATC and then plays its own feedback.
 *
 * @extends {Component<StickyAtcRefs>}
 */
export class StickyAtc extends Component {
  requiredRefs = ['button', 'label'];

  /** @type {HTMLElement | null} */
  #mainAtc = null;
  /** @type {Element | null} */
  #mainAtcLabel = null;
  /** @type {Element | null} */
  #mainAtcAnimationHost = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #stickyFeedbackTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #mainFeedbackTimer;

  /** True between a pill-initiated click and the cart:update it produces. */
  #addPending = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #addPendingTimer;

  /** @type {AbortController | undefined} */
  #controller;
  /** @type {(IntersectionObserver | MutationObserver)[]} */
  #observers = [];

  connectedCallback() {
    super.connectedCallback();

    // Before the early return, not after: the pill is only opacity:0 / pointer-events:none, so
    // with no main ATC to observe its button would otherwise stay focusable for keyboard users.
    this.inert = true;

    const mainAtc = resolveMainAtc();
    if (!mainAtc) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // No container delegation (CLAUDE.md §4). On the host, a click on the price, the compare-at
    // price or the role="img" rating also fired an add — activation surface far wider than the
    // only element carrying an accessible name.
    this.refs.button.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });

    this.#bindMainAtc(mainAtc);
  }

  /**
   * The Section Rendering API can replace the main ATC (a variant swap re-renders the product
   * form). Everything derived from it — the label node, the animation host and the observers —
   * would then point at a detached element, leaving the pill permanently hidden.
   */
  updatedCallback() {
    super.updatedCallback();
    const mainAtc = resolveMainAtc();
    if (!mainAtc || mainAtc === this.#mainAtc) return;
    this.#bindMainAtc(mainAtc);
  }

  /**
   * Point the pill at a main ATC and rebuild everything derived from it. Re-entrant: the
   * observers are torn down first, and the listeners bound in connectedCallback are not touched.
   *
   * @param {HTMLElement} mainAtc
   */
  #bindMainAtc(mainAtc) {
    this.#mainAtc = mainAtc;
    this.#mainAtcLabel = mainAtc.querySelector('span:last-of-type');
    this.#mainAtcAnimationHost = mainAtc.closest('[data-add-to-cart-animation]');

    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];

    this.#watchMainAtcVisibility();
    this.#syncPrice();
    this.#syncCompare();
    this.#syncLabel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];
    clearTimeout(this.#stickyFeedbackTimer);
    clearTimeout(this.#mainFeedbackTimer);
    clearTimeout(this.#addPendingTimer);
  }

  /**
   * The product flies to whichever cart is on screen: the floating cart that appears once the
   * smart header scrolls away, otherwise the header's own cart icon.
   *
   * @returns {Element | null}
   */
  #resolveCartTarget() {
    const floatingCart = document.querySelector('.floating-cart');
    if (isVisible(floatingCart)) {
      return floatingCart?.querySelector('.header-actions__cart-icon') ?? floatingCart;
    }
    return (
      document.querySelector('#header-component .header-actions__cart-icon') ||
      document.querySelector('.header-actions__cart-icon')
    );
  }

  /**
   * Mirror the shop product card's add feedback: the pill label slides up to reveal a checkmark,
   * and a product thumbnail flies into the cart. The configurator's own ATC isn't an
   * <add-to-cart-component>, so this is the only place the fly-to-cart runs.
   */
  #flyToCart() {
    const cartTarget = this.#resolveCartTarget();
    const productImage = this.dataset.productImage;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !cartTarget || !productImage || !customElements.get('fly-to-cart')) return;
    // The fly now launches on the cart response rather than on the click, so the user may have
    // scrolled back up and hidden the pill in between. Flying out of an invisible origin looks
    // like a glitch, so skip it — the destination is already visibility-checked above.
    if (!isVisible(this.refs.button)) return;

    const fly = document.createElement('fly-to-cart');
    fly.style.setProperty('background-image', `url(${productImage})`);
    // @ts-expect-error — properties defined by the fly-to-cart element.
    fly.source = this.refs.button;
    // @ts-expect-error
    fly.destination = cartTarget;
    // Flatten the arc: the pill sits at the bottom and the cart is up top, so a gentle lift that
    // never rises above the cart keeps the thumbnail from being clipped.
    // @ts-expect-error
    fly.arc = { liftStart: 60, liftEnd: 0, pullEnd: 100 };
    document.body.appendChild(fly);
  }

  #playAddedFeedback() {
    const { button } = this.refs;
    if (!button) return;
    // Restart the slide animation even on rapid re-clicks.
    button.classList.remove('is-added');
    void button.offsetWidth;
    button.classList.add('is-added');
    this.#flyToCart();
    clearTimeout(this.#stickyFeedbackTimer);
    this.#stickyFeedbackTimer = setTimeout(() => button.classList.remove('is-added'), FEEDBACK_DURATION);
  }

  /**
   * The main ATC fires its OWN fly-to-cart from the (scrolled-away) button — either via
   * <add-to-cart-component>.handleClick (standard PDP) or product-form-component.handleSubmit
   * (configurators). Both read the data-add-to-cart-animation flag synchronously during the
   * proxied click, so we flip it off around the click to suppress that fly and restore it right
   * after, leaving only the pill's fly.
   */
  #handleClick = () => {
    const mainAtc = /** @type {HTMLButtonElement | null} */ (this.#mainAtc);
    // Sold out / mid-request: the real button is the source of truth, so mirror its disabled
    // state rather than proxying a click that cannot succeed.
    if (!mainAtc || mainAtc.disabled || mainAtc.getAttribute('aria-disabled') === 'true') return;

    // AddToCartComponent.handleClick returns early on an invalid form, which produces no request
    // and therefore no cart:update. Mirror that check so the pending flag is never armed for a
    // click that cannot resolve — otherwise it stays set until the timeout and an unrelated
    // update inside that window plays a false success.
    const form = mainAtc.closest('form');
    if (form && !form.checkValidity()) {
      // Report, don't just bail: clicking the real button runs a native submit and so shows the
      // browser's validation bubble, while the proxied click would otherwise be a dead tap with
      // the offending control (e.g. an emptied quantity input) left off-screen and unfocused.
      form.reportValidity();
      return;
    }

    const host = /** @type {HTMLElement | null} */ (this.#mainAtcAnimationHost);
    const prevAnimation = host?.dataset.addToCartAnimation;
    if (host) host.dataset.addToCartAnimation = 'false';

    this.#addPending = true;
    // If the add never produces a cart:update at all (network failure), drop the flag so a later
    // unrelated update can't animate the pill retroactively.
    clearTimeout(this.#addPendingTimer);
    this.#addPendingTimer = setTimeout(() => (this.#addPending = false), ADD_PENDING_TIMEOUT);

    mainAtc.click();

    if (host) {
      if (prevAnimation === undefined) delete host.dataset.addToCartAnimation;
      else host.dataset.addToCartAnimation = prevAnimation;
    }
  };

  /**
   * A cart error resolves the pill's click just as a cart:update does. Without this the flag
   * stays armed for the full timeout, and an unrelated mutation inside that window (a coupon, a
   * quantity edit) plays the checkmark and the fly-to-cart for an add that never happened.
   */
  #handleCartError = () => {
    this.#addPending = false;
  };

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.didError) {
      // A failed add must not leave the pill claiming success.
      this.#addPending = false;
      return;
    }

    // Success feedback belongs here, not in #handleClick: at click time the cart request has not
    // resolved, so animating there shows a false success whenever the add fails. Gated on
    // #addPending so an add made elsewhere on the page doesn't animate the pill.
    if (this.#addPending) {
      this.#addPending = false;
      this.#playAddedFeedback();
    }

    // The main CTA mirror must only react to an actual add. cart:update also fires for coupon
    // apply/remove ('cart-drawer-discount'), quantity edits ('cart-items-component') and addon
    // checkboxes — without this, applying a coupon in the drawer made the PDP's primary button
    // announce "added". The configurator's CTA has no added-state markup of its own, so this
    // mirror stays for direct clicks too and cannot be folded into the #addPending gate.
    if (detail?.data?.source !== ADD_SOURCE) return;
    // ...and only to an add of THIS product: every product form on the page reports the same
    // source, so a quick-add from a related-product card would otherwise swap the PDP's own CTA.
    if (String(detail.data.productId ?? '') !== (this.dataset.productId ?? '')) return;

    const mainAtc = /** @type {HTMLElement} */ (this.#mainAtc);
    const label = this.#mainAtcLabel;
    const addedLabel = this.dataset.addedLabel;
    // Read the live copy, not a value captured at connect: #syncLabel exists precisely because
    // the CTA's label changes at runtime, so a stale capture would restore page-load copy.
    const previousLabel = label?.textContent ?? '';
    // Only swap when we actually have copy — blanking the CTA for 1.5s is worse than not
    // swapping — and restore under the same condition, so a blank data-added-label cannot
    // overwrite a label it never replaced.
    const swapped = Boolean(label && addedLabel);
    if (label && addedLabel) label.textContent = addedLabel;
    mainAtc.classList.add('is-added');

    clearTimeout(this.#mainFeedbackTimer);
    this.#mainFeedbackTimer = setTimeout(() => {
      if (label && swapped) label.textContent = previousLabel;
      mainAtc.classList.remove('is-added');
    }, FEEDBACK_DURATION);
  };

  /** Show the pill only once the main ATC has scrolled off the top of the viewport. */
  #watchMainAtcVisibility() {
    // Hidden until the observer says otherwise — see connectedCallback for why `inert` and not
    // just the CSS. Re-asserted here so a rebind can't leave a stale value behind.
    this.inert = true;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const isAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('sticky-atc-d--active', isAbove);
          this.inert = !isAbove;
        }
      },
      { threshold: 0 }
    );
    observer.observe(/** @type {HTMLElement} */ (this.#mainAtc));
    this.#observers.push(observer);
  }

  /**
   * Mirror `source` into `apply` on every text change, so the pill tracks the configurator's
   * reactive totals.
   *
   * @param {Element | null} source
   * @param {() => void} apply
   */
  #mirror(source, apply) {
    if (!source) return;
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(source, { childList: true, characterData: true, subtree: true });
    this.#observers.push(observer);
  }

  #syncPrice() {
    const totalEl = document.querySelector('.product-configurator [data-cfg="total"]');
    const { price } = this.refs;
    if (!price) return;
    this.#mirror(totalEl, () => {
      const txt = totalEl?.textContent?.trim();
      if (txt) price.textContent = txt;
    });
  }

  /**
   * Mirror the header's reactive compare-at (UVP) price into the pill so the strikethrough stays
   * in sync with addon/tier selections on every template.
   */
  #syncCompare() {
    const headerCompareEl = document.querySelector('.product-details-header__discount-price');
    const { compare } = this.refs;
    if (!compare) return;

    const compareNumberRegex = /[\d.,]+/;
    const original = compare.textContent ?? '';
    this.#mirror(headerCompareEl, () => {
      const match = headerCompareEl?.textContent?.match(compareNumberRegex);
      if (match) compare.textContent = original.replace(compareNumberRegex, match[0]);
    });
  }

  /**
   * Keep the pill's label in step with the CTA it duplicates, so a merchant-edited cta_text (or a
   * sold-out/unavailable label) shows on both. Gated on data-mirror-label: outside the primary
   * language cta_text is untranslated, so the server-rendered locale value wins.
   */
  #syncLabel() {
    if (this.dataset.mirrorLabel !== 'true') return;
    const { label } = this.refs;
    const mainLabel = this.#mainAtcLabel;
    if (!label || !mainLabel) return;

    this.#mirror(mainLabel, () => {
      // Skip the transient "added" swap in #handleCartUpdate — the pill shows its own added state.
      if (this.#mainAtc?.classList.contains('is-added')) return;
      const txt = mainLabel.textContent?.trim();
      if (txt) label.textContent = txt;
    });
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
